import { html, LitElement, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import '../components/comments';
import { DATA_CLASSES, DATA_VERSIONS } from '../data/sources';
import '../parts/card';
import '../parts/monster';
import '../parts/affix';

import style from './stats.scss';
import { setTitle, trackPage } from '../shared/utils';

export class StatsPage extends LitElement {

    static get properties() {
        return {
            stats: Object
        }
    }

    constructor() {
        super();
        this.stats = null;
        this.diff = '';
        this.class = '';
        this.ver = '';
        this.mode = '';
    }

    static styles = css`
        ${unsafeCSS(style)}        
    `;

    connectedCallback() {
        super.connectedCallback();
        trackPage();
        let params = {
            diff: '',
            class: '',
            ver: '',
            mode: ''
        };
        (window.location.search||'').replace('?','').split('&').forEach(p=>{
            let arr = p.split('=');
            if(arr.length === 2)
                params[arr[0]] = arr[1];
        });
        this.diff = params.diff;
        this.class = params.class;
        this.ver = params.ver;
        this.mode = params.mode;
        setTitle('Game Stats');
        this.fetchData();
    }

    async fetchData() {
        if(!this.diff && !this.class && !this.ver && !this.mode){
            this.ver = DATA_VERSIONS[0];
        }
        let res = await fetch(`./api/stats?diff=${this.diff}&class=${this.class}&ver=${this.ver}&mode=${this.mode}`);
        if (res.status === 200) {
            this.stats = await res.json();
        }
    }

    updateURL(){
        if(window.history){                        
            window.history.replaceState(null, document.title, `./stats?diff=${this.diff}&class=${this.class}&ver=${this.ver}&mode=${this.mode}`);
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
    }

    displayPercentage(top,bottom){
        return `${Math.round(top/(bottom||1)*100)}%`;
    }

    parseClass(name) {
        if (name === 'War')
            return 'Warrior';
        if (name === 'Arc')
            return 'Arcanist';
        if (name === 'Rog')
            return 'Rogue';
        if (name === 'Hun')
            return 'Hunter';
        if (name === 'See')
            return 'Seeker';
        if (name === 'Kni')
            return 'Knight';
        if (name === 'Sci')
            return 'Sunforge Run';
    }

    render() {
        if (!this.stats)
            return html`Crunching the numbers....`;        
        const bestcls = this.parseClass(this.stats.classes[0]?._id);
        const cls = DATA_CLASSES.filter(c=>c.name===bestcls)[0];  
        const totalruns = this.stats.goodruns+this.stats.badruns;          
        return html` 
            <h1>Game Stats</h1>
            <p>Note: This data is aggregated from submitted decks from Discord and Blightbane.</p>
            <select @change=${(ev)=>{
                    this.diff = ev.target.value;        
                    this.updateURL();            
                    this.fetchData();
                }}>
                <option value="">-- Difficulty --</option>
                ${['Normal','Challenging','Hard','Impossible'].map((val,i)=>{
                    if(val)
                        return html`<option value="${val}" ?selected=${this.diff === val}>${val}</option>`;
                })}
            </select>  
            <select @change=${(ev)=>{
                    this.class = ev.target.value;      
                    this.updateURL();              
                    this.fetchData();
                }}>
                <option value="">-- Class --</option>
                ${[...DATA_CLASSES,{name:'Sunforge',value:'Scion'}].map((val,i)=>{
                    if(val)
                        return html`<option value="${val.value || val.name}" ?selected=${this.class === (val.value || val.name)}>${val.name}</option>`;
                })}
            </select>       
            <select @change=${(ev)=>{
                    this.ver = ev.target.value;  
                    this.updateURL();                  
                    this.fetchData();
                }}>
                <option value="">-- Version --</option>
                ${DATA_VERSIONS.map((val,i)=>{
                    if(val)
                        return html`<option value="${val}" ?selected=${this.ver === val}>${val}</option>`;
                })}
            </select>      
            <select @change=${(ev)=>{
                    this.mode = ev.target.value;  
                    this.updateURL();                  
                    this.fetchData();
                }}>
                <option value="">-- Filters --</option>
                ${['WinsOnly','LossesOnly'].map((val,i)=>{
                    if(val)
                        return html`<option value="${val}" ?selected=${this.mode === val}>${val}</option>`;
                })}
            </select>        
            ${this.stats.cards.length === 0 ? html`
                <h3>No data found, please change your filters</h3>
            `:
            html`
            <h3>Most Played</h3>
            ${!this.class ? html`
            <div class="row">
                <div class="bubble big">Class</div>     
                ${cls?html`
                    <img loading="lazy" class="class-image" src='./images/classes/${cls?.artwork}.webp' alt=${cls?.name} />     
                `:html`
                    <img loading="lazy" class="class-image" src='./images/icons/cardart_5_50.webp' alt="Sunforge Run" />     
                `}                                      
                <div style="vertical-align:middle;display:inline-block">
                    ${bestcls !== 'Sunforge Run' ? html`
                        <a href="./class/${bestcls}">${bestcls}</a>
                    `:html`
                        <b>${bestcls}</b>
                    `}                    
                </div>                
                <div class="bubble">${this.displayPercentage(this.stats.classes[0].count,totalruns)}</div>
            </div>
            `:''}            
            <div class="row">
                <div class="bubble big">Card</div>
                <card-part id=${this.stats.cards[0]?._id}></card-part>                
                <div class="bubble">${this.displayPercentage(this.stats.cards[0]?.count,totalruns)}</div>
            </div>
            <div class="row">
                <div class="bubble big">Talent</div>
                <card-part id=${this.stats.talents[0]?._id} talent="true"></card-part>                
                <div class="bubble">${this.displayPercentage(this.stats.talents[0]?.count,totalruns)}</div>
            </div>
            ${this.stats.imbues.length > 0 ? html`
            <div class="row">
                <div class="bubble big">Imbue</div>                
                <card-part id=${this.stats.imbues[0]?._id}></card-part>                
                <div class="bubble">${this.displayPercentage(this.stats.imbues[0]?.count,totalruns)}</div>                            
            </div>
            `:''}
            ${this.stats.malignances.length > 0 ? html`
            <div class="row">
                <div class="bubble big">Malignance</div>                
                <affix-part id=${this.stats.malignances[0]?._id}></affix-part>                
                <div class="bubble">${this.displayPercentage(this.stats.malignances[0]?.count,totalruns)}</div>                            
            </div>
            `:''}    
            <h3>General Stats</h3>
            <div class="row">               
                <div class="bubble big">Total Runs</div> ${totalruns?.toLocaleString()}
            </div>
            <div class="row">               
                <div class="bubble big">Total Score</div> ${this.stats.score[0].total.toLocaleString()}
            </div>
            <div class="row">               
                <div class="bubble big">Total Duration</div> ${this.stats.duration.toLocaleString()} hrs
            </div>
            ${!this.mode ? html`
            <div class="row">               
                <div class="bubble big">Win %</div> ${this.displayPercentage(this.stats.goodruns,totalruns)}
            </div>
            <div class="row">               
                <div class="bubble big">Loss %</div> ${this.displayPercentage(this.stats.badruns,totalruns)}
            </div>
            `:''}                        
            ${!this.class ? html`
            <h3>Most Played Classes</h3>          
            ${this.stats.classes.map((b,i)=>{
                const subcls = this.parseClass(b._id);
                const cls = DATA_CLASSES.filter(c=>c.name===subcls)[0];       
                return html`
            <div class="row">    
                <div class="bubble big">#${i+1}</div> 
                ${cls?html`
                    <img loading="lazy" class="class-image" src='./images/classes/${cls.artwork}.webp' alt=${cls.name} style='margin-left: 5px' />          
                `:html`
                    <img loading="lazy" class="class-image" src='./images/icons/cardart_5_50.webp' alt="Sunforge Run" style='margin-left: 5px' />     
                `}                
                <div style="vertical-align:middle;display:inline-block">
                    ${subcls !== 'Sunforge Run' ? html`
                        <a href="./class/${subcls}">${subcls}</a>
                    `:html`
                        <b>${subcls}</b>
                    `}                        
                </div>                
                <div class="bubble">${this.displayPercentage(b.count,totalruns)}</div>
            </div>
            `;
            })}
            `:''}
            <h3>Most Played Malignancies</h3>          
            ${this.stats.malignances.filter((b)=>b._id!==null).map((b,i)=>html`
            <div class="row">            
                <div class="bubble big">#${i+1}</div>                     
                <affix-part id=${b._id}></affix-part>                
                <div class="bubble">${this.displayPercentage(b.count,totalruns)}</div>
            </div>
            `)}
            <h3>Most Played Cards</h3>          
            ${this.stats.cards.filter((b)=>b._id!==null).map((b,i)=>html`
            <div class="row">            
                <div class="bubble big">#${i+1}</div>                     
                <card-part id=${b._id}></card-part>                
                <div class="bubble">${this.displayPercentage(b.count,totalruns)}</div>
            </div>
            `)}
            <h3>Most Played Talents</h3>          
            ${this.stats.talents.filter((b)=>b._id!==null).map((b,i)=>html`
            <div class="row">              
                <div class="bubble big">#${i+1}</div>                   
                <card-part id=${b._id} talent="true"></card-part>                
                <div class="bubble">${this.displayPercentage(b.count,totalruns)}</div>
            </div>
            `)}
            <h3>Most Played Imbues</h3>          
            ${this.stats.imbues.filter((b)=>b._id!==null).map((b,i)=>html`
            <div class="row">                           
                <div class="bubble big">#${i+1}</div>      
                <card-part id=${b._id}></card-part>                
                <div class="bubble">${this.displayPercentage(b.count,totalruns)}</div>
            </div>
            `)} 
            <h3>Boss Kills</h3>          
            ${this.stats.bosses.filter(b=>b._id !== 'Guild Assassin').map((b,i)=>html`
            <div class="row">                  
                <monster-part id=${b._id}></monster-part>
                <div class="bubble">${b.count} kills</div>
            </div>
            `)}              
            ${this.stats.slainby.filter(b=>b._id !== 'Guild Assassin').map((b,i)=>html`
            ${i === 0 ? html`<h3>Slain By</h3>`:''}
            <div class="row">                  
                <monster-part id=${b._id}></monster-part>
                <div class="bubble">${b.count} players slain</div>
            </div>
            `)}
            `
            }                  
        `;
    }
}

customElements.define('stats-page', StatsPage);